// =====================================================
// SERVIÇO DE INFORMAÇÕES DO SERVIDOR
// =====================================================

import { IA_API_BASE_URL } from './iaApiBase'

const API_BASE_URL = IA_API_BASE_URL
const LOGIN_EMAIL = import.meta.env.VITE_IAAPI_LOGIN_EMAIL || ''
const LOGIN_PASSWORD = import.meta.env.VITE_IAAPI_LOGIN_PASSWORD || ''

// Tipos para as respostas da API
export interface LoginResponse {
  success: boolean
  data: {
    user: {
      id: string
      email: string
      role: string
      createdAt: string
      updatedAt: string
    }
    accessToken: string
    refreshToken: string
  }
  timestamp: string
}

export interface PM2Process {
  name: string
  status: string
  uptime: number
  memory: number
  cpu: number
  restarts: number
}

export interface NginxProject {
  name: string
  serverName: string
  port: number
  status: string
  configFile: string
  root: string
}

export interface ServerInfoResponse {
  success: boolean
  data: {
    pm2: {
      processes: PM2Process[]
      total: number
    }
    nginx: {
      projects: NginxProject[]
      total: number
    }
  }
  timestamp: string
}

export interface ApiResponse<T> {
  data: T | null
  success: boolean
  error?: string
}

// Função para fazer login e obter token
export const serverInfoService = {
  async login(): Promise<ApiResponse<string>> {
    try {
      // Validar se as credenciais estão configuradas
      if (!LOGIN_EMAIL || !LOGIN_PASSWORD) {
        return {
          data: null,
          success: false,
          error: 'Credenciais da API iaapi não configuradas no .env. Configure VITE_IAAPI_LOGIN_EMAIL e VITE_IAAPI_LOGIN_PASSWORD',
        }
      }

      const response = await fetch(`${API_BASE_URL}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: LOGIN_EMAIL,
          password: LOGIN_PASSWORD,
        }),
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.message || `Erro HTTP: ${response.status}`)
      }

      const data: LoginResponse = await response.json()

      if (data.success && data.data?.accessToken) {
        return { data: data.data.accessToken, success: true }
      }

      throw new Error('Token não encontrado na resposta')
    } catch (error) {
      return {
        data: null,
        success: false,
        error: (error as Error).message || 'Erro ao fazer login',
      }
    }
  },

  // Função para buscar informações do servidor
  async getServerInfo(token: string): Promise<ApiResponse<ServerInfoResponse['data']>> {
    try {
      const response = await fetch(`${API_BASE_URL}/server/info`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.message || `Erro HTTP: ${response.status}`)
      }

      const data: ServerInfoResponse = await response.json()

      if (data.success && data.data) {
        return { data: data.data, success: true }
      }

      throw new Error('Dados não encontrados na resposta')
    } catch (error) {
      return {
        data: null,
        success: false,
        error: (error as Error).message || 'Erro ao buscar informações do servidor',
      }
    }
  },

  /** Login na iaapi + chamada acessar-link-auth para obter link do shop (corretor) */
  async getShopLink(codigocorretor: number): Promise<ApiResponse<string>> {
    try {
      const loginResult = await this.login()
      if (!loginResult.success || !loginResult.data) {
        return {
          data: null,
          success: false,
          error: loginResult.error || 'Erro ao autenticar',
        }
      }

      const response = await fetch(`${API_BASE_URL}/shop/acessar-link-auth`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${loginResult.data}`,
        },
        body: JSON.stringify({ codigocorretor }),
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.message || `Erro HTTP: ${response.status}`)
      }

      const data = await response.json()
      const link = data?.link
      if (link && typeof link === 'string' && link.trim()) {
        return { data: link.trim(), success: true }
      }
      throw new Error('Link não encontrado na resposta')
    } catch (error) {
      return {
        data: null,
        success: false,
        error: (error as Error).message || 'Erro ao gerar link',
      }
    }
  },

  /** Login na iaapi + chamada acessar-link-auth para obter link do dashboard corretor */
  async getDashcorretorLink(codigocorretor: number): Promise<ApiResponse<string>> {
    try {
      const loginResult = await this.login()
      if (!loginResult.success || !loginResult.data) {
        return {
          data: null,
          success: false,
          error: loginResult.error || 'Erro ao autenticar',
        }
      }

      const response = await fetch(`${API_BASE_URL}/dashcorretor/acessar-link-auth`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${loginResult.data}`,
        },
        body: JSON.stringify({ codigocorretor }),
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.message || `Erro HTTP: ${response.status}`)
      }

      const data = await response.json()
      const link = data?.link
      if (link && typeof link === 'string' && link.trim()) {
        return { data: link.trim(), success: true }
      }
      throw new Error('Link não encontrado na resposta')
    } catch (error) {
      return {
        data: null,
        success: false,
        error: (error as Error).message || 'Erro ao gerar link',
      }
    }
  },

  // Função combinada que faz login e busca informações
  async getServerInfoWithAuth(): Promise<ApiResponse<ServerInfoResponse['data']>> {
    try {
      // Primeiro, fazer login
      const loginResult = await this.login()

      if (!loginResult.success || !loginResult.data) {
        return {
          data: null,
          success: false,
          error: loginResult.error || 'Erro ao autenticar',
        }
      }

      // Depois, buscar informações do servidor
      const serverInfoResult = await this.getServerInfo(loginResult.data)

      return serverInfoResult
    } catch (error) {
      return {
        data: null,
        success: false,
        error: (error as Error).message || 'Erro ao buscar informações do servidor',
      }
    }
  },
}
